import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Check, Share2, Mail, Send, Image as ImageIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { toPng } from "html-to-image";
import { QRCodeSVG } from "qrcode.react";

/**
 * WhatsAppInviteCard — shows a shareable ?ref= link tied to the current user
 * plus a one-tap WhatsApp share button with a prepared message.
 */
export default function WhatsAppInviteCard() {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [copyingImage, setCopyingImage] = useState(false);
  const cardRef = useRef(null);

  const avatarSrc = useMemo(() => {
    const seed = user?.user_id || user?.name || "u";
    return user?.picture || `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(seed)}`;
  }, [user]);

  const firstName = useMemo(() => (user?.name || "Um amigo").split(" ")[0], [user]);

  const inviteUrl = useMemo(() => {
    if (!user?.user_id) return "";
    try {
      const origin = process.env.REACT_APP_PUBLIC_APP_URL || window.location.origin;
      return `${origin}/?ref=${encodeURIComponent(user.user_id)}`;
    } catch {
      return `/?ref=${user.user_id}`;
    }
  }, [user]);

  const [inviteCfg, setInviteCfg] = useState({ invite_message: "", referred_signup_coins: 50 });
  useEffect(() => {
    const fetchCfg = () => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/settings/public`, { params: { _t: Date.now() } })
      .then((r) => setInviteCfg({ invite_message: r.data.invite_message || "", referred_signup_coins: r.data.referred_signup_coins ?? 50 }))
      .catch(() => {});
    fetchCfg();
    window.addEventListener("focus", fetchCfg);
    return () => window.removeEventListener("focus", fetchCfg);
  }, []);

  const message = useMemo(() => {
    if (!inviteUrl) return "";
    const coins = inviteCfg.referred_signup_coins;
    const template = inviteCfg.invite_message || (
      `🎁 Ganhe moedas no *FUTURO PDV* comigo!\n\n` +
      `Você recebe *+{coins} moedas* de bônus só por criar sua conta, e eu também! ` +
      `Poste, curta, chame corrida ou faça freelas — tudo vira moeda que dá pra sacar.\n\n` +
      `Meu link: {link}`
    );
    const filled = template.replaceAll("{link}", inviteUrl).replaceAll("{coins}", String(coins)).replaceAll("{name}", firstName);
    return filled.includes(inviteUrl) ? filled : `${filled}\n\n${inviteUrl}`;
  }, [inviteUrl, inviteCfg, firstName]);

  const waHref = useMemo(
    () => (message ? `https://wa.me/?text=${encodeURIComponent(message)}` : "#"),
    [message],
  );
  const tgHref = useMemo(
    () => (message ? `https://t.me/share/url?url=${encodeURIComponent(inviteUrl)}&text=${encodeURIComponent(message)}` : "#"),
    [message, inviteUrl],
  );
  const mailHref = useMemo(
    () => (message ? `mailto:?subject=${encodeURIComponent("Vem para o FUTURO PDV comigo 🎁")}&body=${encodeURIComponent(message)}` : "#"),
    [message],
  );

  const copyLink = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      toast.success("Link copiado");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Não consegui copiar automaticamente");
    }
  };

  const shareableImage = async () => {
    if (!cardRef.current) return;
    setCopyingImage(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#ffffff",
      });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "futuropdv-convite.png", { type: "image/png" });
      // Prefer native share sheet (mobile) with the file attached + message.
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: "Convite FUTURO PDV",
            text: message,
          });
          toast.success("Convite compartilhado");
          setCopyingImage(false);
          return;
        } catch { /* user canceled or failed → fall back */ }
      }
      // Try to write to the clipboard (desktop Chrome/Edge).
      if (navigator.clipboard && window.ClipboardItem) {
        try {
          await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
          toast.success("Imagem copiada — cole em qualquer chat");
          setCopyingImage(false);
          return;
        } catch { /* fall back to download */ }
      }
      // Final fallback: trigger a download so the user can attach it manually.
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = "futuropdv-convite.png";
      link.click();
      toast.success("Imagem baixada");
    } catch (e) {
      toast.error("Não consegui gerar a imagem");
    }
    setCopyingImage(false);
  };

  if (!inviteUrl) return null;

  return (
    <Card className="p-5 relative overflow-hidden border-emerald-500/30" data-testid="whatsapp-invite-card">
      <div aria-hidden className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(600px circle at 0% 0%, rgba(16,185,129,.15), transparent 40%)" }} />

      <div className="relative flex items-start gap-3 mb-3">
        <div className="w-11 h-11 rounded-2xl bg-emerald-500/15 text-emerald-600 flex items-center justify-center shrink-0">
          <Share2 className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Traga um amigo · +50 moedas para cada</div>
          <h3 className="text-lg font-bold tracking-tight">Convide pelo WhatsApp</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Mensagem já pronta com seu link de indicação. Quem entrar por ele te dá +50 moedas.
          </p>
        </div>
      </div>

      <div className="relative rounded-xl border border-border bg-muted/40 px-3 py-2 flex items-center gap-2 mb-3">
        <span
          className="text-xs font-mono truncate flex-1 text-muted-foreground"
          data-testid="whatsapp-invite-link"
          title={inviteUrl}
        >
          {inviteUrl}
        </span>
        <button
          onClick={copyLink}
          className="p-1.5 rounded-md hover:bg-muted transition-colors shrink-0"
          data-testid="whatsapp-copy-link"
          aria-label="Copiar link"
        >
          {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>

      <a
        href={waHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-share-btn"
        className="relative inline-flex items-center justify-center gap-2 w-full h-11 rounded-full bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white font-bold text-sm shadow-lg transition-colors"
      >
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden>
          <path d="M20.52 3.48A11.86 11.86 0 0012 0C5.37 0 0 5.37 0 12c0 2.12.55 4.13 1.52 5.92L0 24l6.24-1.63A11.94 11.94 0 0012 24c6.63 0 12-5.37 12-12 0-3.19-1.25-6.19-3.48-8.52zM12 22a9.9 9.9 0 01-5.05-1.39l-.36-.21-3.7.97.99-3.6-.24-.37A9.86 9.86 0 012.1 12C2.1 6.55 6.55 2.1 12 2.1S21.9 6.55 21.9 12 17.45 22 12 22zm5.44-7.4c-.3-.15-1.77-.87-2.05-.97-.28-.1-.48-.15-.68.15-.2.3-.78.97-.96 1.17-.18.2-.35.22-.65.07-.3-.15-1.28-.47-2.43-1.5-.9-.8-1.5-1.79-1.68-2.09-.18-.3-.02-.46.13-.61.13-.13.3-.35.45-.53.15-.18.2-.3.3-.5.1-.2.05-.38-.02-.53-.08-.15-.68-1.63-.93-2.23-.24-.58-.5-.5-.68-.51h-.58c-.2 0-.53.08-.8.38-.28.3-1.05 1.03-1.05 2.5s1.07 2.9 1.22 3.1c.15.2 2.1 3.2 5.1 4.49.71.31 1.26.5 1.7.63.71.23 1.36.2 1.87.12.57-.09 1.77-.72 2.02-1.42.25-.7.25-1.3.18-1.42-.08-.13-.28-.2-.58-.35z" />
        </svg>
        Convidar pelo WhatsApp
      </a>

      {/* Secondary share channels */}
      <div className="grid grid-cols-3 gap-2 mt-2 relative" data-testid="invite-secondary-actions">
        <a
          href={tgHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="invite-telegram"
          className="inline-flex items-center justify-center gap-1 h-9 rounded-full border border-sky-500/40 bg-sky-500/10 text-sky-700 hover:bg-sky-500/20 transition-colors text-xs font-semibold"
        >
          <Send className="w-3.5 h-3.5" /> Telegram
        </a>
        <a
          href={mailHref}
          data-testid="invite-email"
          className="inline-flex items-center justify-center gap-1 h-9 rounded-full border border-indigo-500/40 bg-indigo-500/10 text-indigo-700 hover:bg-indigo-500/20 transition-colors text-xs font-semibold"
        >
          <Mail className="w-3.5 h-3.5" /> E-mail
        </a>
        <button
          onClick={shareableImage}
          disabled={copyingImage}
          data-testid="invite-copy-image"
          className="inline-flex items-center justify-center gap-1 h-9 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-700 hover:bg-amber-500/20 transition-colors text-xs font-semibold disabled:opacity-60"
        >
          {copyingImage ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
          {copyingImage ? "Gerando…" : "Imagem"}
        </button>
      </div>

      {/* Off-screen printable card used as source for the PNG export */}
      <div className="absolute -left-[9999px] top-0 pointer-events-none" aria-hidden>
        <div
          ref={cardRef}
          data-testid="invite-image-source"
          style={{
            width: 640,
            padding: 40,
            background: "linear-gradient(135deg,#f59e0b 0%,#f97316 25%,#dc2626 55%,#7c3aed 85%,#2563eb 100%)",
            color: "#fff",
            fontFamily: "-apple-system, Segoe UI, Roboto, sans-serif",
            borderRadius: 32,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 18 }}>
            <img
              crossOrigin="anonymous"
              src={avatarSrc}
              alt=""
              data-testid="invite-image-avatar"
              style={{
                width: 68, height: 68, borderRadius: 22, objectFit: "cover",
                border: "3px solid rgba(255,255,255,0.75)",
                boxShadow: "0 10px 24px rgba(0,0,0,0.25)",
                background: "#fff",
              }}
              onError={(e) => {
                const seed = encodeURIComponent(user?.user_id || user?.name || "u");
                e.currentTarget.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${seed}`;
              }}
            />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, letterSpacing: 3, fontWeight: 800, opacity: 0.9 }}>FUTURO PDV · CONVITE</div>
              <div style={{ fontSize: 20, fontWeight: 900, marginTop: 4, lineHeight: 1.05 }}>
                {firstName} te convidou 👋
              </div>
              <div style={{ fontSize: 12, opacity: 0.9, marginTop: 2 }}>
                Ganhe <strong>+50 moedas</strong> só por entrar no time.
              </div>
            </div>
          </div>
          <div style={{ fontSize: 30, fontWeight: 900, lineHeight: 1.1 }}>
            Ganhe moedas por tudo que você faz.
          </div>
          <div style={{ fontSize: 15, marginTop: 10, opacity: 0.95 }}>
            Poste, curta, chame corrida ou faça freelas — tudo vira moeda que dá pra sacar.
          </div>
          <div style={{
            marginTop: 24, padding: "14px 18px", borderRadius: 16,
            background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.35)",
            display: "flex", alignItems: "center", gap: 16,
          }}>
            <div style={{
              width: 108, height: 108, borderRadius: 16,
              background: "#fff", padding: 10, display: "flex",
              alignItems: "center", justifyContent: "center", flexShrink: 0,
            }} data-testid="invite-image-qr">
              <QRCodeSVG
                value={inviteUrl}
                size={88}
                level="M"
                includeMargin={false}
                bgColor="#ffffff"
                fgColor="#0f172a"
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, letterSpacing: 2, fontWeight: 800, opacity: 0.85 }}>APONTE A CÂMERA OU ACESSE</div>
              <div style={{ fontSize: 14, fontWeight: 700, wordBreak: "break-all", marginTop: 6 }}>{inviteUrl}</div>
              <div style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 8 }}>
                <span style={{
                  width: 32, height: 32, borderRadius: 10, background: "#fde047",
                  display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#78350f", fontWeight: 900,
                }}>50</span>
                <span style={{ fontSize: 13, fontWeight: 700 }}>+50 moedas grátis</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
